/**
 * Custom React reconciler for JSX-to-string instruction rendering.
 *
 * Uses the shared core JSX reconciler factory (createJsxReconciler) to build
 * an internal tree from JSX, then serialize to formatted text with
 * priority-ordered XML sections.
 *
 * @example
 * ```tsx
 * const output = renderInstructions(
 *   <>
 *     <Identity priority={10}>You are Atlas...</Identity>
 *     <Constraints priority={20}>
 *       <ul>
 *         <li>Never fabricate information</li>
 *       </ul>
 *     </Constraints>
 *   </>
 * );
 * ```
 */

import type { ReactNode } from 'react';

import { arrayChildrenStrategy } from '#core/lib/jsx-reconciler/arrayChildrenStrategy.ts';
import {
  createJsxReconciler,
  type ElementType,
  type JsxReconcilerStrategy,
  type Props,
} from '#core/lib/jsx-reconciler/createJsxReconciler.ts';

import type {
  Node,
  ParentNode,
  SectionAttrs,
  TableCellHeaderNode,
  TableCellNode,
  TableNode,
  TableRowNode,
  TextNode,
  TranscriptMessageNode,
  TranscriptRole,
} from './types.ts';

type KnownTag =
  | 'instruction'
  | 'example'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'header'
  | 'ul'
  | 'ol'
  | 'li'
  | 'p'
  | 'pre'
  | 'dl'
  | 'dt'
  | 'dd'
  | 'strong'
  | 'b'
  | 'em'
  | 'i'
  | 'code'
  | 'table'
  | 'thead'
  | 'tbody'
  | 'tr'
  | 'th'
  | 'td'
  | 'transcript-message';

/** camelCase → kebab-case for wire tag and attribute names. */
const kebabCase = (s: string): string => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

const escapeAttrValue = (s: string): string =>
  s.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

/**
 * Project element props to serializable section attrs: structural keys
 * and non-primitive values are dropped, `false`/nullish omit the
 * attribute (so `cond && value` props compose naturally in JSX).
 */
const sectionAttrs = (props: Props): SectionAttrs =>
  Object.fromEntries(
    Object.entries(props).filter(
      ([key, value]) =>
        key !== 'children' &&
        key !== 'key' &&
        (typeof value === 'string' || typeof value === 'number' || value === true),
    ),
  ) as SectionAttrs;

/**
 * ` name="value"` pairs; `true` renders as a bare attribute. Attrs are
 * sorted by serialized name so the wire format is deterministic — JSX
 * prop order is not stable (lint auto-sorts props alphabetically).
 */
const serializeAttrs = (attrs: SectionAttrs): string =>
  Object.entries(attrs)
    .map(([key, value]) =>
      value === true ? ` ${kebabCase(key)}` : ` ${kebabCase(key)}="${escapeAttrValue(String(value))}"`,
    )
    .sort()
    .join('');
const nodeCreators = {
  instruction: ({ tag, ...props }) => ({
    kind: 'section',
    tag: String(tag ?? ''),
    children: [],
    attrs: sectionAttrs(props),
  }),
  h1: () => ({ kind: 'header', level: 1, children: [] }),
  h2: () => ({ kind: 'header', level: 2, children: [] }),
  h3: () => ({ kind: 'header', level: 3, children: [] }),
  h4: () => ({ kind: 'header', level: 4, children: [] }),
  h5: () => ({ kind: 'header', level: 5, children: [] }),
  h6: () => ({ kind: 'header', level: 6, children: [] }),
  header: () => ({ kind: 'section-header', children: [] }),
  ul: () => ({ kind: 'list', ordered: false, children: [] }),
  ol: () => ({ kind: 'list', ordered: true, children: [] }),
  li: () => ({ kind: 'list-item', children: [] }),
  p: () => ({ kind: 'paragraph', children: [] }),
  pre: () => ({ kind: 'pre', children: [] }),
  example: (props) => ({
    kind: 'example',
    name: props.name == null ? undefined : String(props.name),
    children: [],
  }),
  dl: () => ({ kind: 'definition-list', children: [] }),
  dt: () => ({ kind: 'definition-term', children: [] }),
  dd: () => ({ kind: 'definition-description', children: [] }),
  strong: () => ({ kind: 'strong', children: [] }),
  b: () => ({ kind: 'strong', children: [] }),
  em: () => ({ kind: 'emphasis', children: [] }),
  i: () => ({ kind: 'emphasis', children: [] }),
  code: () => ({ kind: 'inline-code', children: [] }),
  table: () => ({ kind: 'table', children: [] }),
  thead: () => ({ kind: 'table-head', children: [] }),
  tbody: () => ({ kind: 'table-body', children: [] }),
  tr: () => ({ kind: 'table-row', children: [] }),
  th: () => ({ kind: 'table-cell-header', children: [] }),
  td: () => ({ kind: 'table-cell', children: [] }),
  'transcript-message': (props) => ({
    kind: 'transcript-message',
    role: props.role as TranscriptRole,
    children: [],
  }),
} satisfies Record<KnownTag, (props: Props) => Node>;

const createNode = (type: ElementType, props: Props): Node => {
  if (typeof type === 'string') {
    if (Object.hasOwn(nodeCreators, type)) {
      return nodeCreators[type as KnownTag](props);
    }
    if (type.startsWith('tag-')) {
      return { kind: 'section', tag: kebabCase(type.slice(4)), children: [], attrs: sectionAttrs(props) };
    }
  }
  return { kind: 'fragment', children: [] };
};

const isParentNode = (node: Node): node is ParentNode => 'children' in node;
/**
 * Node-creation and tree-wiring strategy for the instructions JSX reconciler.
 * The shared core factory owns all other host-config boilerplate; the
 * array-children wiring subset comes from {@link arrayChildrenStrategy}.
 */
const instructionsStrategy: JsxReconcilerStrategy<Node, TextNode, ParentNode> = {
  ...arrayChildrenStrategy<Node, TextNode, ParentNode>(isParentNode),

  // Instance creation
  createInstance: (type, props) => createNode(type, props),
  createTextInstance: (text): TextNode => ({ kind: 'text', value: text }),

  // Tree building
  appendInitialChild: (parentInstance, child) => {
    if (isParentNode(parentInstance)) {
      parentInstance.children.push(child);
    }
  },
  appendChild: (parentInstance, child) => {
    if (isParentNode(parentInstance)) {
      parentInstance.children.push(child);
    }
  },

  // Updates - commitUpdate signature changed in newer types
  commitUpdate: (instance, _type, _prevProps, nextProps) => {
    if (instance.kind === 'section' && nextProps.tag != null) {
      instance.tag = String(nextProps.tag);
    }
    if (instance.kind === 'example' && nextProps.name !== undefined) {
      instance.name = nextProps.name == null ? undefined : String(nextProps.name);
    }
    if (instance.kind === 'transcript-message' && nextProps.role != null) {
      instance.role = nextProps.role as TranscriptMessageNode['role'];
    }
  },
};

const instructionsReconciler = createJsxReconciler<Node, TextNode, ParentNode, ParentNode>(instructionsStrategy);

/**
 * Sentinel marking a pre-formatted region in serialized output.
 *
 * `<pre>` wraps its body with a matched pair of these so the final
 * whitespace pass in {@link normalizeOutput} can skip it — multi-space
 * collapse and space-before-punctuation stripping would otherwise destroy
 * code indentation. NUL is used because it cannot appear in source text.
 */
const RAW_SENTINEL = '\x00';

/**
 * Final whitespace normalization, applied once at the root.
 *
 * Splits on the {@link RAW_SENTINEL} so segments inside pre-formatted
 * regions are preserved verbatim while prose segments get the cleanup
 * that compensates for JSX whitespace stripping.
 */
const normalizeOutput = (s: string): string =>
  s
    .split(RAW_SENTINEL)
    .map((segment, i) =>
      i % 2 === 0
        ? segment
            .replace(/[ \t]{2,}/g, ' ')
            // .replace(/ ([,;.!?:)\]}])/g, '$1')
            .replace(/\n{3,}/g, '\n\n')
        : segment,
    )
    .join('');

/**
 * Raw join — preserves literal whitespace. Used for `<pre>` and other
 * pre-formatted contexts where indentation and runs of spaces matter.
 */
const serializeRawChildren = (children: Node[]): string => children.map(serializeNode).join('');

/**
 * Inline-aware join.
 *
 * JSX strips newlines adjacent to tags (whitespace rule #4), so an
 * element separated from a following sibling only by a source newline
 * loses the natural separator. This join re-injects a single space at
 * those boundaries — but ONLY when at least one side is a non-text
 * node (an element). Two adjacent **text** nodes are emitted verbatim:
 * JSX already baked the author's intended spacing into the string
 * literals, so `{a}{b}` / `{n} step{plural}` must stay glued (inserting
 * a space there produced bugs like `( 5 step s ,`). A synthetic space
 * is likewise suppressed when the right side opens with closing/sep
 * punctuation (`,`, `.`, `)`, …) so `<code>x</code>,` stays `\`x\`,`.
 *
 * Final whitespace normalization (multi-space collapse, 3+-newline
 * collapse) is applied once at the root by {@link normalizeOutput};
 * doing it here would recurse into already-serialized `<pre>` content
 * and destroy code indentation.
 */
const NO_SPACE_BEFORE = /^[,;.!?:)\]}]/;

const serializeChildren = (children: Node[]): string =>
  children.reduce(
    (acc, node) => {
      const part = serializeNode(node);
      if (!part) {
        return acc;
      }
      const isText = node.kind === 'text';
      if (acc.joined === '') {
        return { joined: part, prevWasText: isText };
      }
      const leftEnd = acc.joined.slice(-1);
      const rightStart = part.charAt(0);
      const glue =
        /\s/.test(leftEnd) || /\s/.test(rightStart) || (acc.prevWasText && isText) || NO_SPACE_BEFORE.test(part);
      return { joined: acc.joined + (glue ? part : ` ${part}`), prevWasText: isText };
    },
    { joined: '', prevWasText: false },
  ).joined;

const escapeTableCell = (s: string): string => s.replace(/\n/g, ' ').replace(/\|/g, '\\|');

const tableRowCells = (row: TableRowNode): string[] =>
  row.children
    .filter((c): c is TableCellNode | TableCellHeaderNode => c.kind === 'table-cell-header' || c.kind === 'table-cell')
    .map((c) => escapeTableCell(serializeChildren(c.children).trim()));

const partitionTableRows = (children: Node[]): { headRows: TableRowNode[]; bodyRows: TableRowNode[] } => {
  const headRows: TableRowNode[] = [];
  const bodyRows: TableRowNode[] = [];
  for (const child of children) {
    if (child.kind === 'table-head' || child.kind === 'table-body') {
      const target = child.kind === 'table-head' ? headRows : bodyRows;
      for (const row of child.children) {
        if (row.kind === 'table-row') {
          target.push(row);
        }
      }
    } else if (child.kind === 'table-row') {
      bodyRows.push(child);
    }
  }
  // HTML-table convention: if no <thead> was given but the first body
  // row uses <th> cells, treat that row as the header.
  const firstBody = bodyRows[0];
  if (headRows.length === 0 && firstBody && firstBody.children.some((c) => c.kind === 'table-cell-header')) {
    headRows.push(firstBody);
    bodyRows.shift();
  }
  return { headRows, bodyRows };
};

const serializeTable = (node: TableNode): string => {
  const { headRows, bodyRows } = partitionTableRows(node.children);
  const headCells = headRows.flatMap(tableRowCells);
  const bodyCellRows = bodyRows.map(tableRowCells);
  const colCount = Math.max(headCells.length, ...bodyCellRows.map((r) => r.length), 1);
  const pad = (cells: string[]): string[] =>
    cells.length >= colCount ? cells.slice(0, colCount) : [...cells, ...Array(colCount - cells.length).fill('')];
  const headerCells =
    headCells.length > 0 ? pad(headCells) : Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`);
  const lines = [
    `| ${headerCells.join(' | ')} |`,
    `| ${Array(colCount).fill('---').join(' | ')} |`,
    ...bodyCellRows.map((row) => `| ${pad(row).join(' | ')} |`),
  ];
  return `\n\n${lines.join('\n')}\n\n`;
};

/**
 * Per-kind serialization for the {@link Node} union — a narrowing chain
 * terminated by `satisfies never`, so adding a `Node` variant is a
 * COMPILE ERROR here until a branch is supplied.
 */
const serializeNode = (node: Node): string => {
  if (node.kind === 'text') {
    return node.value;
  }
  if (node.kind === 'section') {
    const attrs = serializeAttrs(node.attrs);
    const contents = serializeChildren(node.children).trim();
    if (!contents.length) {
      return `<${node.tag}${attrs} />\n`;
    }
    return `<${node.tag}${attrs}>${contents.includes('\n') ? `\n${contents}\n` : contents}</${node.tag}>\n`;
  }
  if (node.kind === 'section-header') {
    return `\n**${serializeChildren(node.children).trim()}:**\n\n`;
  }
  if (node.kind === 'header') {
    return `\n\n${'#'.repeat(node.level)} ${serializeChildren(node.children).trim()}\n\n`;
  }
  if (node.kind === 'paragraph') {
    return `\n\n${serializeChildren(node.children).trim()}\n\n`;
  }
  if (node.kind === 'pre') {
    return `\`\`\`${node.language ?? ''}\n${RAW_SENTINEL}${serializeRawChildren(node.children).trim()}${RAW_SENTINEL}\n\`\`\`\n\n`;
  }
  if (node.kind === 'list') {
    return `${node.children
      .map((child, i) => {
        const bullet = node.ordered ? `${i + 1}.` : '-';
        return `${bullet} ${serializeNode(child).trim()}`;
      })
      .join('\n')}\n`;
  }
  if (node.kind === 'list-item') {
    return serializeChildren(node.children);
  }
  if (node.kind === 'example') {
    return `\n\n<example workflow="${node.name}">\n${serializeChildren(node.children).trim()}\n</example>\n\n`;
  }
  if (node.kind === 'definition-list') {
    // A <dt> opens an entry; every <dd> up to the next <dt> belongs
    // to it (a term may have multiple descriptions — e.g. an op's
    // signature, prose, per-kwarg lines, and a code example). A <dd>
    // with no preceding <dt> is a free-standing description.
    type Entry = { readonly term: string | null; readonly descs: string[] };
    const entries: Entry[] = [];
    for (const child of node.children) {
      if (child.kind === 'definition-term') {
        entries.push({ term: serializeChildren(child.children).trim(), descs: [] });
        continue;
      }
      if (child.kind !== 'definition-description') {
        continue;
      }
      const desc = serializeChildren(child.children).trim();
      const open = entries.at(-1);
      if (open === undefined || open.term === null) {
        entries.push({ term: null, descs: [desc] });
      } else {
        open.descs.push(desc);
      }
    }
    // A description is "block" when it is a fenced code region (a
    // `<pre>` example): it must sit on its own lines, never glued to
    // a `- ` bullet or indented as inline continuation.
    const isBlock = (d: string): boolean => d.startsWith('```') || d.includes(RAW_SENTINEL);
    const renderEntry = (e: Entry): string => {
      if (e.term === null) {
        return e.descs.map((d) => (isBlock(d) ? `\n${d}\n` : `- ${d}`)).join('\n');
      }
      if (e.descs.length === 0) {
        return `- ${e.term}`;
      }
      // Compact form: a lone inline description stays on the term
      // line (the shape ToolGuidance / RuleList emit).
      if (e.descs.length === 1 && e.descs[0] !== undefined && !isBlock(e.descs[0])) {
        return `- ${e.term}: ${e.descs[0]}`;
      }
      const body = e.descs
        .map((d) => (isBlock(d) ? `\n${d}\n` : `  - ${d}`))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n');
      return `- ${e.term}\n${body}`;
    };
    return `${entries.map(renderEntry).join('\n')}\n`;
  }
  if (node.kind === 'definition-term' || node.kind === 'definition-description') {
    return serializeChildren(node.children);
  }
  if (node.kind === 'strong') {
    return `*${serializeChildren(node.children).trim()}*`;
  }
  if (node.kind === 'emphasis') {
    return `_${serializeChildren(node.children).trim()}_`;
  }
  if (node.kind === 'inline-code') {
    return `\`${serializeChildren(node.children).trim()}\``;
  }
  if (node.kind === 'fragment') {
    return serializeChildren(node.children);
  }
  if (node.kind === 'table') {
    return serializeTable(node);
  }
  if (
    node.kind === 'table-head' ||
    node.kind === 'table-body' ||
    node.kind === 'table-row' ||
    node.kind === 'table-cell-header' ||
    node.kind === 'table-cell'
  ) {
    return serializeChildren(node.children);
  }
  if (node.kind === 'transcript-message') {
    // Boundaries are consumed structurally by `renderTranscript`; one
    // reaching the serializer means it was nested inside other content.
    throw new Error('<transcript-message> must be a top-level transcript element — found nested inside content');
  }
  return node satisfies never;
};

/**
 * Render a JSX tree to the reconciler's root node, surfacing render-time
 * throws. React's sync reconciler reports them through the error callbacks
 * instead of propagating out of flushSyncWork; capturing the first error and
 * rethrowing ensures a throwing node can never silently collapse to empty
 * output (which a downstream blank filter would then drop without a trace).
 * Serialization must run only AFTER this guard, so a render-time throw
 * surfaces as the wrapped cause rather than being masked by a serializer
 * error walking a partially-committed tree.
 *
 * `captureCaughtErrors` controls whether errors an error boundary already
 * handled still fail the render. Instructions renders have no boundaries,
 * so they stay strict; the transcript render passes `false` — its
 * per-record boundaries degrade a throwing record to visible fallback
 * prose, which must not fail the surrounding thread render.
 */
function renderToRootNode(element: ReactNode, opts: { captureCaughtErrors?: boolean } = {}): ParentNode {
  const { captureCaughtErrors = true } = opts;
  const captured: { error?: unknown; has: boolean } = { has: false };
  const captureError = (error: unknown) => {
    if (!captured.has) {
      captured.has = true;
      captured.error = error;
    }
  };
  const root = instructionsReconciler.renderSync(
    element,
    (): ParentNode => ({ kind: 'fragment', children: [] }),
    (rendered) => rendered,
    {
      onUncaughtError: captureError,
      onCaughtError: captureCaughtErrors
        ? captureError
        : (error: unknown) =>
            console.error('[reconciler] render error caught by boundary — degraded to fallback', error),
    },
  );

  if (captured.has) {
    throw new Error('instructions reconciler failed to render node', { cause: captured.error });
  }

  return root;
}
/**
 * Renders JSX instructions to a formatted string.
 *
 * Content is serialized in document order. `<tag-foo>` / `<instruction tag="foo">`
 * elements wrap their children in `<foo>…</foo>` XML. Other elements produce
 * markdown-style formatting (headers, lists, paragraphs).
 */
export function renderInstructions(element: ReactNode): string {
  return normalizeOutput(serializeChildren(renderToRootNode(element).children)).trim();
}

/** One role-tagged message split out of a transcript render — structurally a `ModelMessage`. */
export type TranscriptMessage = {
  [RoleType in TranscriptRole]: { role: RoleType; content: string };
}[TranscriptRole];

/**
 * Renders a transcript JSX tree to role-tagged messages.
 *
 * The top level of the tree must consist solely of `<transcript-message role>`
 * boundaries (whitespace-only text between them is ignored) — any other
 * top-level content throws: a transcript turn without an explicit role is a
 * structural bug, not a degradation case. Each boundary's body serializes
 * exactly like {@link renderInstructions} output; blank messages are dropped.
 */
export function renderTranscript(element: ReactNode): TranscriptMessage[] {
  return renderToRootNode(element, { captureCaughtErrors: false })
    .children.map((node) => {
      if (node.kind === 'text' && node.value.trim() === '') {
        return null;
      }
      if (node.kind !== 'transcript-message') {
        throw new Error(`renderTranscript: top-level content outside <transcript-message> (kind "${node.kind}")`);
      }
      const content = normalizeOutput(serializeChildren(node.children)).trim();
      return content.length > 0 ? ({ role: node.role, content } as TranscriptMessage) : null;
    })
    .filter((m) => m !== null);
}
